import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Lock, Images, Link2, Trash2 } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";
import GalleryCreateForm from "./GalleryCreateForm";
import GalleryManager from "./GalleryManager";

const miniBtn = "inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-[9px] font-mono tracking-[0.15em] uppercase transition-colors";

export const galleryLink = (slug) => `${window.location.origin}/gallery/${slug}`;

export default function AdminGalleries({ onLogout }) {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/galleries");
      setGalleries(data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upsert = (g) => setGalleries((list) => (list.some((x) => x.id === g.id) ? list.map((x) => (x.id === g.id ? g : x)) : [g, ...list]));

  const copyLink = async (slug) => {
    await navigator.clipboard.writeText(galleryLink(slug));
    toast.success("Gallery link copied");
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/galleries/${id}`);
      setGalleries((list) => list.filter((g) => g.id !== id));
      toast.success("Gallery deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (active) {
    return <GalleryManager gallery={active} onBack={() => { setActive(null); load(); }} onUpdated={(g) => { upsert(g); setActive(g); }} />;
  }

  if (creating) {
    return <GalleryCreateForm onCancel={() => setCreating(false)} onCreated={(g) => { upsert(g); setCreating(false); setActive(g); }} />;
  }

  return (
    <div data-testid="admin-galleries-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">Client Galleries</p>
          <h2 className="mt-2 font-serif text-xl text-ivory">{galleries.length} private galler{galleries.length === 1 ? "y" : "ies"}</h2>
        </div>
        <button data-testid="gallery-new-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-champagne text-obsidian px-6 py-3 text-[11px] font-mono tracking-[0.2em] uppercase hover:bg-ivory transition-colors">
          <Plus size={14} /> New Gallery
        </button>
      </div>

      {loading && galleries.length === 0 ? (
        <p data-testid="galleries-loading" className="mt-16 text-[11px] font-mono tracking-[0.3em] uppercase text-stone-500 animate-pulse">Loading galleries…</p>
      ) : galleries.length === 0 ? (
        <p data-testid="galleries-empty" className="mt-16 font-serif text-xl italic text-stone-500">No client galleries yet — create one for a booked couple.</p>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="galleries-list">
          {galleries.map((g) => (
            <article key={g.id} data-testid={`gallery-card-${g.id}`} className="border border-white/10 bg-elevated p-5 flex flex-col">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[9px] font-mono tracking-[0.2em] uppercase text-champagne truncate">{g.couple || "—"}</p>
                  <h3 className="mt-1 font-serif text-lg text-ivory leading-tight truncate">{g.title}</h3>
                </div>
                <Lock size={14} className="shrink-0 text-gold mt-1" />
              </div>
              <p className="mt-3 text-[10px] font-mono text-stone-500 truncate">/gallery/{g.slug}</p>
              <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-light text-stone-400"><Images size={12} /> {g.photo_count} photos</p>
              <div className="mt-5 flex flex-wrap gap-1.5">
                <button data-testid={`gallery-manage-${g.id}`} onClick={() => setActive(g)} className={`${miniBtn} border-champagne text-champagne hover:bg-champagne hover:text-obsidian`}>Manage</button>
                <button data-testid={`gallery-copy-${g.id}`} onClick={() => copyLink(g.slug)} className={`${miniBtn} border-white/15 text-stone-300 hover:border-champagne hover:text-champagne`}><Link2 size={11} /> Copy Link</button>
                {confirmId === g.id ? (
                  <button data-testid={`gallery-delete-confirm-${g.id}`} onClick={() => remove(g.id)} className={`${miniBtn} border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian`}>Confirm</button>
                ) : (
                  <button data-testid={`gallery-delete-${g.id}`} onClick={() => setConfirmId(g.id)} className={`${miniBtn} border-white/15 text-stone-500 hover:border-terracotta hover:text-terracotta`} aria-label="Delete gallery"><Trash2 size={11} /></button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
